import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

function PrivacyPolicy() {
    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = "Privacy Policy | Kwagala Moris Owen";
    }, []);

    return (
        <div className="min-h-screen bg-dark-bg text-white font-sans selection:bg-green-500/30">
            {/* Background Decorations */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-green-500/10 rounded-full blur-[120px] animate-pulse"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-500/5 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '2s' }}></div>
            </div>

            {/* Navigation Bar */}
            <nav className="fixed top-0 left-0 right-0 z-50 glass-nav py-4 border-b border-white/5">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 flex justify-between items-center">
                    <Link to="/" className="flex items-center gap-2 group">
                        <div className="w-10 h-10 rounded-xl bg-green-500 text-black flex items-center justify-center font-black text-lg transition-transform group-hover:-translate-x-1">
                            <i className="bi bi-arrow-left"></i>
                        </div>
                        <span className="font-bold text-lg tracking-tight">Back to Portfolio</span>
                    </Link>
                    <div className="hidden md:block">
                        <span className="text-gray-500 text-sm font-medium uppercase tracking-widest">Privacy Policy</span>
                    </div>
                </div>
            </nav>

            {/* Content Area */}
            <main className="relative z-10 pt-32 pb-20 px-6">
                <div className="max-w-4xl mx-auto">
                    {/* Header Section */}
                    <div className="mb-16">
                        <h1 className="text-5xl md:text-7xl font-black mb-6 tracking-tighter">
                            Privacy <span className="text-green-500">Policy</span>.
                        </h1>
                        <p className="text-gray-400 text-lg md:text-xl max-w-2xl leading-relaxed">
                            Your privacy is important. This policy explains how information is handled when you visit this portfolio.
                        </p>
                        <div className="mt-8 flex items-center gap-4">
                            <span className="px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-bold uppercase tracking-widest text-gray-400">
                                Last Updated: March 2026
                            </span>
                        </div>
                    </div>

                    {/* Policy Content */}
                    <div className="glass-card rounded-[2rem] p-8 md:p-12 border border-white/5 space-y-12">

                        <section>
                            <h2 className="text-2xl font-bold mb-4 text-white flex items-center gap-3">
                                <span className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center text-green-500 text-sm">01</span>
                                Information Collection
                            </h2>
                            <p className="text-gray-400 leading-relaxed text-lg">
                                This is a personal portfolio website. I do not automatically collect any personally identifiable information (PII) from visitors. No cookies are used for tracking purposes by this site directly.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4 text-white flex items-center gap-3">
                                <span className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center text-green-500 text-sm">02</span>
                                Contact Form
                            </h2>
                            <p className="text-gray-400 leading-relaxed text-lg">
                                If you choose to use the contact form, the information you provide (name, email, message) is sent directly to my email address via EmailJS. This information is only used to respond to your inquiry and is not stored in any database or shared with third parties.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4 text-white flex items-center gap-3">
                                <span className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center text-green-500 text-sm">03</span>
                                Data Collection & Usage
                            </h2>
                            <p className="text-gray-400 leading-relaxed text-lg mb-6">
                                Depending on which application or service you use, we may collect and process different types of information to provide and improve our services:
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="p-6 rounded-2xl bg-white/5 border border-white/5">
                                    <h3 className="text-green-500 font-bold mb-2 uppercase tracking-widest text-[10px]">Usage Data</h3>
                                    <p className="text-gray-400 text-sm leading-relaxed">
                                        Information about how you interact with our apps, including features used and session duration, to optimize performance and user experience.
                                    </p>
                                </div>
                                <div className="p-6 rounded-2xl bg-white/5 border border-white/5">
                                    <h3 className="text-green-500 font-bold mb-2 uppercase tracking-widest text-[10px]">Location Data</h3>
                                    <p className="text-gray-400 text-sm leading-relaxed">
                                        If explicitly permitted, we may use location data to provide core services (like logistics or mapping) only when the app is in use.
                                    </p>
                                </div>
                                <div className="p-6 rounded-2xl bg-white/5 border border-white/5">
                                    <h3 className="text-green-500 font-bold mb-2 uppercase tracking-widest text-[10px]">Device Info</h3>
                                    <p className="text-gray-400 text-sm leading-relaxed">
                                        Technical details such as device model, OS version, and unique identifiers to ensure compatibility and security across all platforms.
                                    </p>
                                </div>
                                <div className="p-6 rounded-2xl bg-white/5 border border-white/5">
                                    <h3 className="text-green-500 font-bold mb-2 uppercase tracking-widest text-[10px]">User Input</h3>
                                    <p className="text-gray-400 text-sm leading-relaxed">
                                        Information you voluntarily provide through forms or account settings, used strictly for its intended administrative purpose.
                                    </p>
                                </div>
                            </div>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4 text-white flex items-center gap-3">
                                <span className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center text-green-500 text-sm">04</span>
                                Data Protection & Sharing
                            </h2>
                            <p className="text-gray-400 leading-relaxed text-lg">
                                We do not sell, trade, or rent your personal information to third parties. We only share data with service providers when necessary to perform specific functions on our behalf (e.g., cloud hosting or email delivery), and only under strict confidentiality agreements.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4 text-white flex items-center gap-3">
                                <span className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center text-green-500 text-sm">05</span>
                                Your Rights & Choices
                            </h2>
                            <p className="text-gray-400 leading-relaxed text-lg">
                                You have the right to access, update, or request the deletion of your personal data at any time. You can also opt-out of certain data collection features through your device settings or by contacting us directly.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4 text-white flex items-center gap-3">
                                <span className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center text-green-500 text-sm">06</span>
                                Children's Privacy
                            </h2>
                            <p className="text-gray-400 leading-relaxed text-lg">
                                Our services are not intended for children under 13. We do not knowingly collect personal information from children. If we become aware of such collection, we take immediate steps to remove that information from our servers.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4 text-white flex items-center gap-3">
                                <span className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center text-green-500 text-sm">07</span>
                                Third-Party Links
                            </h2>
                            <p className="text-gray-400 leading-relaxed text-lg">
                                This site and our applications may contain links to external services. We are not responsible for the privacy practices of those sites and encourage you to review their policies independently.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-2xl font-bold mb-4 text-white flex items-center gap-3">
                                <span className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center text-green-500 text-sm">08</span>
                                Security
                            </h2>
                            <p className="text-gray-400 leading-relaxed text-lg">
                                We implement industry-standard security measures to protect your data. However, please note that no method of transmission or storage is 100% secure, and we cannot guarantee absolute data security.
                            </p>
                        </section>

                        <section className="pt-8 border-t border-white/5">
                            <h2 className="text-xl font-bold mb-4 text-white">Questions?</h2>
                            <p className="text-gray-400 leading-relaxed">
                                If you have any questions about this Privacy Policy, please contact me at:
                                <br />
                                <a href="mailto:kwagalamoris@gmail.com" className="text-green-500 hover:underline font-medium mt-2 inline-block">
                                    kwagalamoris@gmail.com
                                </a>
                            </p>
                        </section>
                    </div>

                    <div className="mt-20 text-center">
                        <Link to="/" className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black font-bold rounded-2xl hover:bg-green-500 transition-all duration-300 transform hover:-translate-y-1">
                            Return to Homepage
                            <i className="bi bi-house-fill"></i>
                        </Link>
                    </div>
                </div>
            </main>

            {/* Simple Footer */}
            <footer className="py-12 border-t border-white/5 relative z-10">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <p className="text-gray-600 text-sm font-bold uppercase tracking-widest">
                        &copy; {new Date().getFullYear()} Kwagala Moris Owen. All Rights Reserved.
                    </p>
                </div>
            </footer>
        </div>
    );
}

export default PrivacyPolicy;
